import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES, scrollToId } from "@/lib/assets";
import { useLang } from "@/lib/i18n";

const scrollTo = scrollToId;

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={lineVariant}
      custom={i}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [0.5, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-10">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.football} alt="" className="w-full h-full object-cover object-center grayscale contrast-105 brightness-110" />
        <motion.div style={{ opacity }} className="absolute inset-0 bg-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#0A0A0A]/80 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 md:px-10 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="font-mono-pw uppercase text-xs md:text-sm tracking-[0.3em] text-[#E6FF00] mb-6"
        >
          {t.hero.overline}
        </motion.p>

        <h1 className="font-anton uppercase leading-[0.82] tracking-tight text-[15vw] md:text-[11vw] lg:text-[9.5rem]">
          <Line i={0}>{t.hero.l1}</Line>
          <Line i={1} className="pw-outline-text">{t.hero.l2}</Line>
          <Line i={2}>{t.hero.l3}</Line>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="max-w-md font-body text-white/70 text-base md:text-lg leading-relaxed"
          >
            {t.hero.subPre} <span className="text-white">{t.hero.subStrong}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => scrollTo("reserve")}
              data-testid="hero-cta-btn"
              className="font-mono-pw uppercase text-sm tracking-[0.15em] bg-[#E6FF00] text-black px-8 py-4 font-semibold hover:-translate-y-1 transition-transform duration-200 shadow-[5px_5px_0px_rgba(230,255,0,0.3)]"
            >
              {t.hero.ctaPrimary}
            </button>
            <button
              onClick={() => scrollTo("how")}
              data-testid="hero-secondary-btn"
              className="font-mono-pw uppercase text-sm tracking-[0.15em] border border-white/30 text-white px-8 py-4 hover:border-[#E6FF00] hover:text-[#E6FF00] transition-colors"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      {/* Stats bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="relative z-10 border-y border-white/10 grid grid-cols-2 md:grid-cols-4"
      >
        {t.hero.stats.map((s, i) => (
          <div
            key={i}
            className={`px-5 md:px-10 py-6 md:py-8 ${i !== 0 ? "border-l border-white/10" : ""} ${
              i === 2 ? "max-md:border-t" : ""
            } ${i === 3 ? "max-md:border-t" : ""}`}
          >
            <div className="font-anton text-4xl md:text-6xl text-[#E6FF00] leading-none">{s.k}</div>
            <div className="font-mono-pw uppercase text-[10px] md:text-xs tracking-[0.2em] text-white/50 mt-2">
              {s.v}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
